import { DEPT_ALL, DEPT_OPTIONS, inDept } from "../technicalDept";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Calendar, ChevronRight } from "react-feather";
import SectionCard from "../../../components/common/SectionCard";
import GuardAvatar from "../../guarding/components/roster/GuardAvatar";
import StatusBadge from "../../../components/common/StatusBadge";
import {
  TECHNICAL_STATUS_CLASS, TECHNICAL_STATUS_LABEL, TECHNICAL_SOURCE_LABEL, TECHNICAL_SOURCE_CLASS,
} from "../technicalStatusMaps";
import { useGetTechnicalDutyListQuery } from "../technicalApi";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { MAROON, MAROON_TINT } from "../../../components/common/brandColors";
import { paths } from "../../../routes/paths";

const formatDay = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : null;

const technicianName = (row) => `${row.employeeFirstName || ""} ${row.employeeLastName || ""}`.trim() || "Unnamed";

// Who is carrying what - the SAME assignment data the work list shows, not a
// separate roster or schedule. A caller without Technical.ManageWork is pinned
// to their own rows server-side, so for a manager (the only persona this web
// module serves) it is the whole branch.
//
// 2026-10-02: "should be grouped by customer and techs assigned under the
// customer name, now it is technician wise". Customer is the default view: one
// card per customer, each job under it with the technicians assigned to it.
// The technician view is kept one click away for "what is X carrying".
const GROUP_CUSTOMER = "customer";
const GROUP_TECHNICIAN = "technician";

// The source / status / due line shared by both views.
const WorkMeta = ({ row }) => (
  <div className="d-flex align-items-center flex-wrap mt-1" style={{ gap: 6 }}>
    <span className={`badge ${TECHNICAL_SOURCE_CLASS[row.sourceType] || "badge-linesecondary"}`}>
      {TECHNICAL_SOURCE_LABEL[row.sourceType] || row.sourceType}
    </span>
    <StatusBadge status={row.status} classMap={TECHNICAL_STATUS_CLASS} labelMap={TECHNICAL_STATUS_LABEL} />
    {row.expectedCompletionDate && (
      <span className="text-muted d-inline-flex align-items-center" style={{ fontSize: 11.5 }}>
        <Calendar size={11} className="me-1" />
        due {formatDay(row.expectedCompletionDate)}
      </span>
    )}
  </div>
);

const countBadge = (text) => (
  <span className="badge" style={{ background: MAROON_TINT, color: MAROON, fontWeight: 700 }}>{text}</span>
);
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const DutyListPage = () => {
  const navigate = useNavigate();
  const { selectedBranchId } = useBranchFilter();
  const [activeOnly, setActiveOnly] = useState(true);
  const [groupBy, setGroupBy] = useState(GROUP_CUSTOMER);
  const [dept, setDept] = useState(DEPT_ALL);

  const { data: allRows = [], isLoading, isError, error } = useGetTechnicalDutyListQuery({
    branchId: selectedBranchId ?? undefined,
    activeOnly,
  });

  // Installation / Service first (technicalDept.js); both groupings below work on the result.
  const data = useMemo(() => allRows.filter((r) => inDept(dept, r.sourceType)), [allRows, dept]);
  // The endpoint returns one row per (technician, work) pair, so grouping is
  // the client's job - same shape Flutter's duty screen builds.
  const technicianGroups = useMemo(() => {
    const byTechnician = new Map();
    data.forEach((row) => {
      const key = row.employeeRecordGuid;
      if (!byTechnician.has(key)) {
        byTechnician.set(key, { employeeRecordGuid: key, employeeCode: row.employeeCode, name: technicianName(row), rows: [] });
      }
      byTechnician.get(key).rows.push(row);
    });
    return [...byTechnician.values()].sort((a, b) => b.rows.length - a.rows.length);
  }, [data]);

  // Customer -> jobs -> technicians. The rows carry no customer id, so the customer
  // is keyed on the trimmed, case-folded name (same customer = same name on every job).
  const customerGroups = useMemo(() => {
    const byCustomer = new Map();
    data.forEach((row) => {
      const name = (row.customerName || "").trim() || "No customer";
      const key = name.toLowerCase();
      if (!byCustomer.has(key)) byCustomer.set(key, { key, name, phone: row.customerPhone, works: new Map(), technicians: new Set() });
      const c = byCustomer.get(key);
      if (!c.phone && row.customerPhone) c.phone = row.customerPhone;
      c.technicians.add(row.employeeRecordGuid);
      if (!c.works.has(row.technicalWorkRecordGuid)) c.works.set(row.technicalWorkRecordGuid, { row, technicians: [] });
      const work = c.works.get(row.technicalWorkRecordGuid);
      if (!work.technicians.some((t) => t.employeeRecordGuid === row.employeeRecordGuid)) {
        work.technicians.push({ employeeRecordGuid: row.employeeRecordGuid, employeeCode: row.employeeCode, name: technicianName(row) });
      }
    });
    return [...byCustomer.values()]
      .map((c) => ({
        ...c,
        works: [...c.works.values()]
          .map((w) => ({ ...w, technicians: [...w.technicians].sort((a, b) => a.name.localeCompare(b.name)) }))
          .sort((a, b) => String(a.row.workNumber).localeCompare(String(b.row.workNumber))),
      }))
      .sort((a, b) => b.works.length - a.works.length || a.name.localeCompare(b.name));
  }, [data]);

  const isEmpty = data.length === 0;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex justify-content-between align-items-center w-100 flex-wrap gap-2">
            <div className="page-title">
              <h4>Technicians' Duty List</h4>
              <h6 className="text-muted">Who is carrying what right now</h6>
            </div>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div className="btn-group btn-group-sm" role="group" aria-label="Department">
                {DEPT_OPTIONS.map((o) => (
                  <button
                    key={o.value || "all"}
                    type="button"
                    className="btn"
                    onClick={() => setDept(o.value)}
                    style={dept === o.value ? { background: "#1F5FAD", color: "#fff", borderColor: "#1F5FAD" } : { background: "#fff", color: "#1F5FAD", borderColor: "#1F5FAD" }}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <div className="btn-group btn-group-sm" role="group" aria-label="Group by">
                {[
                  [GROUP_CUSTOMER, "By customer"],
                  [GROUP_TECHNICIAN, "By technician"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className="btn"
                    onClick={() => setGroupBy(value)}
                    style={
                      groupBy === value
                        ? { background: MAROON, color: "#fff", borderColor: MAROON }
                        : { background: "#fff", color: MAROON, borderColor: MAROON }
                    }
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="dutyActiveOnly"
                  checked={activeOnly}
                  onChange={(e) => setActiveOnly(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="dutyActiveOnly">Open work only</label>
              </div>
            </div>
          </div>
        </div>

        {isLoading && <p>Loading...</p>}

        {isError && (
          <div className="alert alert-danger">Could not load the duty list ({error?.message || "server error"}).</div>
        )}

        {!isLoading && !isError && isEmpty && (
          <SectionCard icon={Users} title="Nothing assigned">
            <p className="text-muted mb-0">
              No technician has {activeOnly ? "open " : ""}work assigned in this branch.
            </p>
          </SectionCard>
        )}

        {groupBy === GROUP_CUSTOMER ? (
          <div className="row">
            {customerGroups.map((c) => (
              <div className="col-xl-6" key={c.key}>
                <SectionCard
                  title={
                    <span>
                      {c.name}
                      {c.phone && (
                        <span className="text-muted ms-2" style={{ fontSize: 12, fontWeight: 400 }}>{c.phone}</span>
                      )}
                    </span>
                  }
                  right={countBadge(`${plural(c.works.length, "job")} · ${plural(c.technicians.size, "tech")}`)}
                >
                  {c.works.map(({ row, technicians }) => (
                    <button
                      key={row.technicalWorkRecordGuid}
                      type="button"
                      className="d-flex align-items-center w-100 text-start p-2 mb-2 rounded"
                      style={{ border: `1px solid ${MAROON}20`, background: "#fff" }}
                      onClick={() => navigate(paths.technicalWorkDetail(row.technicalWorkRecordGuid))}
                    >
                      <div className="flex-grow-1 min-width-0">
                        <div className="fw-semibold" style={{ fontSize: 13 }}>{row.workNumber}</div>
                        <WorkMeta row={row} />
                        <div className="d-flex flex-wrap mt-2" style={{ gap: 6 }}>
                          {technicians.map((t) => (
                            <span
                              key={t.employeeRecordGuid}
                              className="d-inline-flex align-items-center rounded-pill pe-2"
                              style={{ background: MAROON_TINT, color: MAROON, fontSize: 12, fontWeight: 600, gap: 6 }}
                              title={`Code ${t.employeeCode}`}
                            >
                              <GuardAvatar employeeRecordGuid={t.employeeRecordGuid} hasPhoto />
                              {t.name}
                            </span>
                          ))}
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-muted flex-shrink-0" />
                    </button>
                  ))}
                </SectionCard>
              </div>
            ))}
          </div>
        ) : (
          <div className="row">
            {technicianGroups.map((group) => (
              <div className="col-xl-6" key={group.employeeRecordGuid}>
                <SectionCard
                  title={
                    <span className="d-inline-flex align-items-center">
                      <span className="me-2"><GuardAvatar employeeRecordGuid={group.employeeRecordGuid} hasPhoto /></span>
                      <span>
                        {group.name}
                        <span className="text-muted ms-2" style={{ fontSize: 12, fontWeight: 400 }}>
                          Code {group.employeeCode}
                        </span>
                      </span>
                    </span>
                  }
                  right={countBadge(plural(group.rows.length, "job"))}
                >
                  {group.rows.map((row) => (
                    <button
                      key={row.technicalWorkRecordGuid}
                      type="button"
                      className="d-flex align-items-center w-100 text-start p-2 mb-2 rounded border-0"
                      style={{ border: `1px solid ${MAROON}20`, background: "#fff" }}
                      onClick={() => navigate(paths.technicalWorkDetail(row.technicalWorkRecordGuid))}
                    >
                      <div className="flex-grow-1 min-width-0">
                        <div className="fw-semibold text-truncate" style={{ fontSize: 13.5 }}>
                          {/* Customer leads, work number is the muted detail (2026-09-12). */}
                          {row.customerName || row.workNumber}{" "}
                          {row.customerName && <span className="text-muted fw-normal">{row.workNumber}</span>}
                        </div>
                        <WorkMeta row={row} />
                      </div>
                      <ChevronRight size={16} className="text-muted flex-shrink-0" />
                    </button>
                  ))}
                </SectionCard>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DutyListPage;
